import { Link, useParams } from 'react-router-dom'
import { PowerOff, ShieldAlert, TriangleAlert } from 'lucide-react'
import Button from '../../components/Button'
import { ROLES } from '../../app/navigation'
import { signOut } from '../../lib/api'
import { useSession } from '../../lib/sessionContext'
import { useResource } from '../../lib/useResource'
import { getErrorPage, getShutdownPage, getUnauthorizedPage } from './systemService'
import { systemPageStrings as S } from './strings'
import './account.css'

/**
 * The three standalone pages the app can land on outside the shell —
 * `/error`, `/unauthorized` and `/shutdown`. They share one surface because the legacy
 * renders all three as a centred card on the auth background.
 *
 * THE WORDS COME FROM THE SERVER. Each of these was a Razor view whose entire content was
 * composed server-side, and the shutdown message even changes with the reader's roles. So
 * the text is fetched (W6) and the strings shipped here are the fallback — used while the
 * request is in flight, and kept when it cannot be answered at all.
 *
 * That last part is the point: these are the screens for when something has already gone
 * wrong. An error page that renders nothing because it could not reach the server would
 * fail exactly when it is needed.
 *
 * @param {'error'|'unauthorized'|'shutdown'} state
 */
export default function SystemPage({ state }) {
  const { session } = useSession()
  const { statusCode } = useParams()
  /* `session` is null in TWO states, not one — while the bootstrap is in flight, and when
     the visitor is anonymous. These four routes sit outside `SessionGate` on purpose, so
     nothing holds the render back until the session resolves and EVERY visitor passes
     through that window. Reading `.roles` unconditionally threw, React tore the tree down,
     and the error page rendered nothing at all — which is the one page that must always
     render something. */
  const isManager = session?.roles?.includes(ROLES.SettingsManagers) ?? false

  const { data } = useResource(
    (signal) => {
      if (state === 'unauthorized') return getUnauthorizedPage(signal)
      if (state === 'shutdown') return getShutdownPage(signal)
      return getErrorPage(statusCode, signal)
    },
    [state, statusCode],
  )

  const view = {
    error: {
      icon: TriangleAlert,
      tone: 'danger',
      title: data?.title || S.error.title,
      lines: data?.lines ?? [...S.error.message.split('\n'), S.error.sorry],
      actions: (
        <>
          <Button as={Link} to="/general">
            {data?.backLabel || S.error.home}
          </Button>
          {/* The legacy footer links to the contact page. */}
          <a className="acct-system__link" href="/ContactUs">
            <bdi>{S.error.contact}</bdi>
          </a>
        </>
      ),
    },
    unauthorized: {
      icon: ShieldAlert,
      tone: 'warning',
      title: data?.title || S.unauthorized.title,
      lines: data?.lines ?? [S.unauthorized.message],
      actions: (
        <Button as={Link} to="/general">
          {data?.backLabel || S.unauthorized.home}
        </Button>
      ),
    },
    shutdown: {
      icon: PowerOff,
      tone: 'dark',
      title: data?.title || S.shutdown.heading,
      /* The licence note and its button are settings-manager only. The server decides
         that when it answers; the session role is the fallback's version of the rule. */
      lines:
        data?.lines ??
        (isManager ? [S.shutdown.message, S.shutdown.managerNote] : [S.shutdown.message]),
      actions: (
        <>
          {(data?.isSettingsManager ?? isManager) && (
            <Button as={Link} to="/license">
              {data?.licenseLabel || S.shutdown.licenseLink}
            </Button>
          )}
          <Button variant="secondary" onClick={signOut}>
            {data?.logoutLabel || S.shutdown.logout}
          </Button>
        </>
      ),
    },
  }[state]

  const Icon = view.icon

  return (
    <main className="acct-system">
      <section className="acct-system__card">
        <span className={`acct-system__icon acct-system__icon--${view.tone}`} aria-hidden="true">
          <Icon size={34} strokeWidth={1.5} />
        </span>

        <h1 className="acct-system__title">
          <bdi>{view.title}</bdi>
        </h1>

        {view.lines.map((line, index) => (
          <p key={index} className="acct-system__line">
            <bdi>{line}</bdi>
          </p>
        ))}

        <div className="acct-system__actions">{view.actions}</div>
      </section>
    </main>
  )
}
